//#region redux
import { AppState } from "store";
import { useSelector } from "react-redux";
//#endregion
//#region components
import { useCheckRelations } from "components/common";
import { useAdvisorPermissions } from "components/advisor";
//#endregion
import { AuthEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";

const useSalaryPermissions = () => {
  const { getIsRevisor, hasRelation } = useCheckRelations();
  const { isAdvisor } = useAdvisorPermissions();
  const isRevisor = getIsRevisor();

  const isSalaryDeviationsAvailable = useSelector(
    (state: AppState) => state.auth.selectedCompany.individualSalaryDeviationsAvailable
  );
  const isSalaryAdmin = useSelector((state: AppState) => state.auth.selectedCompany.loggedInUserIsSalaryAdmin);

  const isCommissionSalaryAvailable = useSelector(
    (state: AppState) => state.auth.selectedCompany.commissionSalaryAvailable
  );

  const getIsGrossSalaryReportAvailable = () => {
    return isRevisor || isAdvisor;
  };

  const isWintSalaryDeviationsAvailable = useSelector(
    (state: AppState) => state.auth.selectedCompany.wintSalaryDeviationsAvailable
  );

  // Core computes this per logged-in user: the company runs Wint.Salary-owned commission, commission
  // is active, and the user is either an enabled broker or a salary admin. It decides which endpoint
  // the commission report reads from; visibility of the page itself is the union of this and the
  // legacy flag, so a company on either commission system keeps its Commission entry.
  const isWintSalaryCommissionAvailable = useSelector(
    (state: AppState) => state.auth.selectedCompany.wintSalaryCommissionAvailable
  );

  const isAnyCommissionSalaryAvailable = isCommissionSalaryAvailable || isWintSalaryCommissionAvailable;

  // A Core without the field would otherwise read as false and drop active Wint Salary companies to the legacy form.
  const isWintSalarySettingsAvailable = useSelector(
    (state: AppState) =>
      state.auth.selectedCompany.wintSalarySettingsAvailable ?? state.auth.selectedCompany.wintSalaryDeviationsAvailable
  );

  const isBusinessUnitManagerWithSalaryRights = useSelector(
    (state: AppState) => state.auth.selectedCompany.loggedInUserIsBusinessUnitManagerWithSalaryRights
  );

  const isWintSalaryCompany = useSelector(
    (state: AppState) =>
      state.auth.selectedCompany.superkollSalaryType === WintSuperkollAPIModel.SuperkollSalaryType.WintSalary
  );

  const hasChartPermission =
    hasRelation(AuthEnums.PersonCompanyRelationType.Reports) || isSalaryAdmin || isBusinessUnitManagerWithSalaryRights;

  return {
    isSalaryAdmin,
    isSalaryDeviationsAvailable,
    isCommissionSalaryAvailable,
    isWintSalaryCommissionAvailable,
    isAnyCommissionSalaryAvailable,
    getIsGrossSalaryReportAvailable,
    isWintSalaryDeviationsAvailable,
    isWintSalarySettingsAvailable,
    isWintSalaryCompany,
    hasChartPermission,
  };
};

export default useSalaryPermissions;
